import { WithStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';
import React, { useCallback, useMemo } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { alertParams, showAlert } from '~/components/common/Alert';
import Table from '~/components/common/Table';
import { TColumn } from '~/components/common/Table/Table';
import { UserStatus, IUser } from '~/models/user';
import themes from '~/themes';
import {
  convertedSortParams,
  getNewSort,
  transformTypeToTitle,
} from '~/utils/common';
import { USER_ROLE, USER_STATUS } from '~/constants/userManagement';
import { updateLocationSearchParams } from '~/utils/formatter';
import { IAlert } from '~/components/common/Alert/Alert';
import {
  useResendInvitationMutation,
  useDeleteUserMutation,
} from '~/services/api/userManagement';
import { queryClient } from '~/lib/react-query';
import { queryKeys } from '~/services/api/queryKeys';
import { errorAlert } from '~/components/common/Alert/alertParams';
import { useAuth } from '~/providers/AuthProvider';
import { ToolTipText } from '~/components/common/Tooltip/TooltipText';
import { fromQueryString } from '~/utils/queryString';
import { ElementSelect } from '~/components/common/ElementSelect/ElementSelect';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Typography } from '@mui/material';

const style = () =>
  createStyles({
    button: {
      cursor: 'pointer',
      textDecoration: 'underline',
    },
    avatar: {
      borderRadius: '50%',
    },
    status: {
      fontSize: 12,
      color: '#F7982A',
      backgroundColor: 'rgba(254, 116, 0, 0.10)',
      borderRadius: '4px',
      padding: '4px 8px',
    },
  });

interface IProps extends WithStyles<typeof style> {
  data: IUser[];
  isLoading: boolean;
}

const UserTable: React.FC<IProps> = ({ classes, data, isLoading }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { account } = useAuth();
  const sourceType =
    useParams<{ sourceType: string; source: string }>()?.sourceType ||
    UserStatus.Active;
  const { sort, sort_by } = fromQueryString(location.search);

  const { mutate: deleteUser, isLoading: loadingUpdate } =
    useDeleteUserMutation({
      onSuccess: () => {
        showAlert({
          ...alertParams.success,
          title: 'User was deleted successfully',
        });
        queryClient.invalidateQueries([queryKeys.userManagement.getUsers]);
      },
      onError: (error) => {
        showAlert(errorAlert(error));
      },
    });

  const { mutate: resendInvitation, isLoading: loadingResent } =
    useResendInvitationMutation({
      onSuccess: () => {
        showAlert({
          ...alertParams.success,
          title: 'Resent Invitation Successfully',
          description:
            'The invitation link has been resent to the user’s email.',
        });
      },
      onError: (error) => {
        showAlert(errorAlert(error));
      },
    });

  const handleSort = useCallback(
    (sortId: string, nextArrange: number) => {
      const sortParams = getNewSort(sortId, nextArrange);
      navigate({
        pathname: location.pathname,
        search: `?${updateLocationSearchParams(location, sortParams)}`,
      });
    },
    [location],
  );

  const onDeleteAlert = useCallback(
    (id: string) => {
      let params: IAlert = {
        ...alertParams.warning,
        title: 'Warning',
        cancelText: 'Cancel',
        okText: 'Yes, Delete',
        onOk: () => deleteUser(id),
      };
      params = {
        ...params,
        description:
          'You are about to delete this user.\nAre you sure you want to continue?',
      };
      showAlert(params);
    },
    [sourceType],
  );

  const renderAction = useCallback(
    ({ id, status, role }: IUser) => {
      if (role === USER_ROLE.OWNER || id === account?.id) {
        return '';
      }

      return (
        <ElementSelect
          paperProps={{
            sx: {
              width: 160,
            },
          }}
          elementSelect={() => (
            <MoreHorizIcon
              sx={{ fontSize: 20, color: themes.color.violet900 }}
            />
          )}
          iconButtonProps={{
            sx: { height: 26, width: 26 },
          }}
          onChange={(o) => {
            if (o.value === 'resend') {
              resendInvitation(id);
            }
            if (o.value === 'delete') {
              onDeleteAlert(id || '');
            }
          }}
          options={[
            ...(status === UserStatus.Pending
              ? [{ label: 'Resend invitation', value: 'resend' }]
              : []),
            { label: 'Delete user', value: 'delete' },
          ]}
        />
      );
    },
    [sourceType, account?.id],
  );

  const columns: TColumn[] = useMemo(
    () => [
      {
        id: 'display_name',
        title: 'Name',
        cellStyle: { minWidth: 150 },
        sortable: true,
        renderComponent: ({ display_name, status }: IUser) => (
          <Typography
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: '4px',
              alignItems: 'center',
            }}
          >
            <ToolTipText text={display_name} maxLength={30} />
            {status === USER_STATUS.PENDING && (
              <span role='presentation' className={classes.status}>
                Pending invite
              </span>
            )}
          </Typography>
        ),
      },
      {
        id: 'role',
        title: 'Role',
        cellStyle: { minWidth: 150 },
        sortable: false,
        renderComponent: ({ role }: IUser) => transformTypeToTitle(role),
      },
      {
        id: 'email',
        title: 'Email',
        cellStyle: { minWidth: 150 },
        sortable: true,
      },
      {
        id: 'action',
        title: '',
        cellStyle: {
          minWidth: 50,
          textAlign: 'right',
        },
        renderComponent: renderAction,
      },
    ],
    [sourceType, account?.id],
  );

  return (
    <Table
      columns={columns}
      dataProps={data}
      noHoverInteraction
      loading={isLoading || loadingUpdate || loadingResent}
      sortBy={convertedSortParams(sort, sort_by)}
      onSort={handleSort}
      headerCellSxProps={{
        'color': themes.color.gray400,
        'backgroundColor': themes.color.gray200,
        'fontSize': 12,
        'paddingTop': '4px !important',
        'paddingBottom': '4px !important',
        'border': 'none',
        '& #name-sortable': {
          display: 'flex !important',
        },
      }}
      cellSxProps={{
        paddingTop: '10px !important',
        paddingBottom: '10px !important',
        color: themes.color.violet500,
      }}
    />
  );
};

export default withStyles(style)(UserTable);
